import React, { useCallback, useState, useEffect, useRef } from "react";
import { Col, Row, Button, Spin, Radio } from "antd";
import {
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
  LeftOutlined,
  ImportOutlined,
  ExportOutlined,
  LoadingOutlined,
  FileOutlined,
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import { BlockManager, BasicType } from "easy-email-core";
import {
  EmailEditor,
  EmailEditorProvider,
  EmailEditorProviderProps,
  IEmailTemplate,
} from "easy-email-editor";
import {
  ConfigProvider,
  Dropdown,
  Menu,
  Message,
  PageHeader,
  Space,
} from "@arco-design/web-react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { brandColors } from "./utils/emailBuilderConfig";
import TemplateGallery from "./components/TemplateGallery";
import EmailBuilderSidebar from "./components/EmailBuilderSidebar";
import enUS from "@arco-design/web-react/es/locale/en-US";

// Import styles
import "easy-email-editor/lib/style.css";
import "easy-email-extensions/lib/style.css";
import "@arco-themes/react-easy-email-theme/css/arco.css";
import "@assets/css/EmailBuilder.css";

// Import custom modules
import { fontList } from "./utils/emailBuilderConfig";
import {
  onImportJSON,
  onImportMJML,
  onImportHTML,
  onExportHTML,
  onExportJSON,
  onExportMJML,
} from "./utils/emailBuilderUtils";
import { uploadImage, fetchTemplate, saveTemplate } from "./utils/apiService";
import EmailPreview from "./HtmlTemplatePreview";

const HtmlTemplateBuilder: React.FC = () => {
  // Router hooks
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isCreateMode = location.pathname.endsWith("/create");

  // State hooks
  const [emailContent, setEmailContent] = useState<string>("");
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [deviceMode, setDeviceMode] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [templateData, setTemplateData] = useState<any>({
    name: "Untitled - HTML Template Builder",
    subject: "What's new in Copilotly?",
    subTitle: "Check it out!",
    content: BlockManager.getBlockByType(BasicType.PAGE)!.create({}),
  });
  const [isEditing, setIsEditing] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateGalleryVisible, setTemplateGalleryVisible] =
    useState<boolean>(false);

  const editableRef = useRef(null);

  // Fetch template data on component mount or when id changes
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const fetchedTemplate = await fetchTemplate(id);
        setTemplateData(fetchedTemplate);
        setTemplateName(fetchedTemplate.name);
      } catch (error) {
        console.error("Error fetching template:", error);
        Message.error("Failed to load template");
      }
    };

    loadTemplate();
  }, [id, isCreateMode]);

  // Function to update template data
  const updateTemplateData = (newData: Partial<IEmailTemplate>) => {
    setTemplateData((prevData: any) => ({
      ...prevData,
      ...newData,
    }));
  };

  const handleSelectTemplate = (template: Partial<IEmailTemplate>) => {
    setTemplateData((prev: any) => ({
      ...prev,
      ...template,
    }));
    Message.success("Template loaded successfully!");
  };

  // Handle image upload
  const onUploadImage = useCallback(async (data: any): Promise<string> => {
    try {
      const imageUrl = await uploadImage(data);
      return imageUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      Message.error("Failed to upload image");
      throw error;
    }
  }, []);

  // Handle form submission
  const onSubmit = useCallback((values: IEmailTemplate) => {
    updateTemplateData(values);
  }, []);

  // Handle preview generation
  const onBeforePreview: EmailEditorProviderProps["onBeforePreview"] =
    useCallback((html: string) => {
      setEmailContent(html);
      return html;
    }, []);

  // Function to save the template
  const onSaveTemplate = async (values: IEmailTemplate) => {
    setIsSaving(true);
    try {
      await saveTemplate(id, values);
      Message.success("Template saved successfully!");
    } catch (error) {
      console.error("Error saving template:", error);
      Message.error("Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  // Function to save the edited title
  const handleSaveTemplateName = async () => {
    if (editableRef.current) {
      const updatedName = editableRef.current.textContent || templateName;

      // Update the local state with the new title
      setTemplateName(updatedName);

      setIsSaving(true);
      try {
        // Save the updated template title to the backend
        await saveTemplate(id, templateData, updatedName);
        Message.success("Template Title saved successfully!");
      } catch (error) {
        console.error("Error saving template title:", error);
        Message.error("Failed to save template title");
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false); // Exit edit mode after saving
  };

  // Spinner indicator
  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 20,
      }}
      spin
    />
  );

  return (
    <ConfigProvider locale={enUS}>
      <div className="email-builder-container">
        <EmailEditorProvider
          height="calc(100vh - 64px)"
          data={templateData}
          fontList={fontList}
          onSubmit={onSubmit}
          onUploadImage={onUploadImage}
          autoComplete
          dashed={false}
          onBeforePreview={onBeforePreview}
          onChange={updateTemplateData}
          extraSettings={{
            colors: brandColors,
          }}
        >
          {({ values }, { restart }) => (
            <>
              {/* Page Header */}
              <PageHeader
                style={{
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                }}
                title={
                  <Space>
                    {/* Back button */}
                    <Button
                      icon={<LeftOutlined />}
                      type="text"
                      size="small"
                      onClick={() => history.goBack()}
                    />

                    {/* Editable Page title */}
                    <span
                      ref={editableRef}
                      contentEditable
                      suppressContentEditableWarning
                      onClick={() => setIsEditing(true)} // Enable edit mode when clicked
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSaveTemplateName();
                          e.preventDefault(); // Prevent new line
                        }
                      }}
                      style={{
                        border: isEditing ? "1px dashed gray" : "none",
                        padding: "3px 10px",
                        margin: "0 0 0 -8px",
                        fontSize: "16px",
                        whiteSpace: "nowrap", // Ensures the content stays in one line
                        overflow: "hidden",
                        textOverflow: "ellipsis", // Handles overflow with ellipsis
                      }}
                    >
                      {isSaving ? (
                        <Spin indicator={antIcon} size="small" />
                      ) : templateName && templateName?.length > 30 ? (
                        templateName?.slice(0, 30) + "..."
                      ) : (
                        templateName
                      )}
                      {!isEditing && (
                        <EditOutlined
                          style={{ marginLeft: "8px", cursor: "pointer" }}
                        />
                      )}
                    </span>

                    {/* Save button (visible only in edit mode) */}
                    {isEditing && (
                      <>
                        <Button
                          type="primary"
                          onClick={handleSaveTemplateName}
                          style={{ marginLeft: "8px", padding: "5px 10px" }}
                          size="small"
                        >
                          Save
                        </Button>
                      </>
                    )}
                  </Space>
                }
                extra={
                  <Space size="large">
                    {/* Template Gallery Button */}
                    <Button
                      icon={<FileOutlined />}
                      type="dashed"
                      size="small"
                      style={{
                        marginRight: "8px",
                        backgroundColor: "#008000",
                        color: "#ffffff",
                      }}
                      onClick={() => setTemplateGalleryVisible(true)}
                    >
                      Template Gallery
                    </Button>

                    {/* Save/Update Template Button */}
                    <Button
                      type="primary"
                      size="small"
                      icon={<SaveOutlined />}
                      loading={isSaving}
                      onClick={() => onSaveTemplate(values)}
                    >
                      {isCreateMode ? "Save Template" : "Update Template"}
                    </Button>

                    {/* Device Mode Selector - Only visible in preview mode */}
                    {previewMode && (
                      <Radio.Group
                        size="small"
                        value={deviceMode}
                        onChange={(e) => setDeviceMode(e.target.value as "desktop" | "tablet" | "mobile")}
                        optionType="button"
                        buttonStyle="solid"
                      >
                        <Radio.Button value="desktop"><DesktopOutlined /></Radio.Button>
                        <Radio.Button value="tablet"><TabletOutlined /></Radio.Button>
                        <Radio.Button value="mobile"><MobileOutlined /></Radio.Button>
                      </Radio.Group>
                    )}

                    {/* Preview/Edit Toggle Button */}
                    <Button
                      icon={previewMode ? <EditOutlined /> : <EyeOutlined />}
                      onClick={() => setPreviewMode(!previewMode)}
                      size="small"
                    >
                      {previewMode ? "Edit" : "Preview"}
                    </Button>
                    {/* Import Dropdown */}
                    <Dropdown
                      droplist={
                        <Menu>
                          <Menu.Item
                            key="HTML"
                            onClick={() => onImportHTML({ restart })}
                          >
                            Import from HTML
                          </Menu.Item>
                          <Menu.Item
                            key="MJML"
                            onClick={() => onImportMJML({ restart })}
                          >
                            Import from MJML
                          </Menu.Item>
                          <Menu.Item
                            key="JSON"
                            onClick={() => onImportJSON({ restart })}
                          >
                            Import from JSON
                          </Menu.Item>
                        </Menu>
                      }
                    >
                      <Button icon={<ImportOutlined />} size="small">
                        Import
                      </Button>
                    </Dropdown>
                    {/* Export Dropdown */}
                    <Dropdown
                      droplist={
                        <Menu>
                          <Menu.Item
                            key="ExportMJML"
                            onClick={() => onExportMJML(values)}
                          >
                            Export MJML
                          </Menu.Item>
                          <Menu.Item
                            key="ExportHTML"
                            onClick={() => onExportHTML(values)}
                          >
                            Export HTML
                          </Menu.Item>
                          <Menu.Item
                            key="ExportJSON"
                            onClick={() => onExportJSON(values)}
                          >
                            Export JSON
                          </Menu.Item>
                        </Menu>
                      }
                    >
                      <Button icon={<ExportOutlined />} size="small">
                        Export
                      </Button>
                    </Dropdown>
                  </Space>
                }
              />
              {/* Main Content Area */}
              <div className="email-builder-content">
                <Row style={{ height: "100%" }}>
                  {/* Sidebar */}
                  <Col flex="380px" className="email-builder-sidebar">
                    <EmailBuilderSidebar compact={false} />
                  </Col>
                  {/* Main Editor/Preview Area */}
                  <Col flex="auto" className="email-builder-main">
                    {previewMode ? (
                      <div className="email-preview-container">
                        <EmailPreview emailContent={emailContent} deviceMode={deviceMode} />
                      </div>
                    ) : (
                      <EmailEditor />
                    )}
                  </Col>
                </Row>
              </div>

              <TemplateGallery
                isVisible={templateGalleryVisible}
                onClose={() => setTemplateGalleryVisible(false)}
                onSelectTemplate={handleSelectTemplate}
              />
            </>
          )}
        </EmailEditorProvider>
      </div>
    </ConfigProvider>
  );
};

export default HtmlTemplateBuilder;
